import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useLeads } from '../../../context/LeadContext';
import leadActivityService from '../../../api/crm/leads/leadActivityService';
import fileUploadService from '../../../api/services/fileUploadService';
import { toast } from '../../../utils/toast';
import {
  ArrowLeft,
  Save,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Linkedin,
  Clock,
  User,
  Building,
  Paperclip,
  Trash2,
  Image
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  sanitizeTextInput,
  sanitizeDescriptionInput,
  validateNumberInput,
  handleTextInputChange,
  handleNumberInputChange,
  getCharacterCount,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';

const LogActivity = ({ leadId: propLeadId, activityId: propActivityId, onBack, onSuccess }) => {
  const navigate = useNavigate();
  const { id: paramLeadId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const editModeParam = searchParams.get('edit') === 'true';
  const activityIdParam = searchParams.get('activityId');

  // Prioritize props over URL params
  const id = propLeadId || paramLeadId;
  const activityId = propActivityId || activityIdParam;
  const isEditMode = !!propActivityId || editModeParam;
  const { getLeadById, updateLead, leads } = useLeads();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [leadData, setLeadData] = useState(null);
  const [isLoadingLead, setIsLoadingLead] = useState(true);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [activityData, setActivityData] = useState(null);

  // Fetch activity data if in edit mode
  useEffect(() => {
    const fetchActivityData = async () => {
      if (isEditMode && activityId) {
        setIsLoadingActivity(true);
        try {
          const result = await leadActivityService.getActivityById(activityId);
          if (result.success && result.activity) {
            setActivityData(result.activity);
            // Pre-fill form with activity data
            // For calls, get duration from callMetadata.talkDuration (in seconds) and convert to minutes
            const activity = result.activity;
            let durationValue = '';
            if (activity.type === 'call' && activity.callMetadata?.talkDuration) {
              // Convert seconds to minutes for display
              durationValue = String(Math.round(activity.callMetadata.talkDuration / 60));
            } else if (activity.duration) {
              durationValue = String(activity.duration);
            }

            setFormData({
              type: activity.type || 'call',
              description: activity.description || '',
              attachments: activity.attachments || []
            });
          } else {
            toast.error(result.error || 'Failed to load activity data');
            if (onBack) {
              onBack();
            } else {
              navigate(location.state?.from || '/lead_activities');
            }
          }
        } catch (error) {

          toast.error('An error occurred while loading activity data');
          if (onBack) {
            onBack();
          } else {
            navigate(location.state?.from || '/lead_activities');
          }
        } finally {
          setIsLoadingActivity(false);
        }
      }
    };

    fetchActivityData();
  }, [isEditMode, activityId, navigate]);

  // Fetch lead data on component mount
  useEffect(() => {
    const fetchLeadData = async () => {
      setIsLoadingLead(true);
      try {
        // In edit mode, use lead from activity data if available
        if (isEditMode && activityData && activityData.lead) {
          const lead = activityData.lead;
          setLeadData({
            id: lead._id || lead.id,
            _id: lead._id || lead.id,
            firstName: lead.firstName,
            lastName: lead.lastName,
            email: lead.email,
            company: lead.company,
            phone: lead.phone
          });
          setIsLoadingLead(false);
          return;
        }

        // First, try to find the lead in the local leads array
        // Normalize IDs for comparison (convert both to strings)
        const normalizedId = String(id).trim();
        const localLead = leads.find(lead => {
          const leadId = String(lead.id || lead._id || lead.leadId || '').trim();
          return leadId === normalizedId;
        });

        if (localLead) {

          setLeadData(localLead);
          setIsLoadingLead(false);
        } else {
          // If not found locally, fetch from API

          const fetchedLead = await getLeadById(id);
          if (fetchedLead) {

            setLeadData(fetchedLead);
          } else {

            setLeadData(null);
          }
          setIsLoadingLead(false);
        }
      } catch (error) {

        setLeadData(null);
        setIsLoadingLead(false);
      }
    };

    if (id) {
      fetchLeadData();
    } else {
      setIsLoadingLead(false);
      setLeadData(null);
    }
  }, [id, getLeadById, leads, isEditMode, activityData]);

  const [formData, setFormData] = useState({
    type: 'call',
    description: '',
    attachments: []
  });

  const [isUploading, setIsUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      // Validate file size (max 25MB)
      const maxSize = 25 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File "${file.name}" exceeds the 25MB size limit.`);
        failCount++;
        continue;
      }

      try {
        const uploadResult = await fileUploadService.uploadToCloudinary(file, 'lead-activities');
        if (uploadResult.success) {
          const newAttachment = {
            fileName: file.name,
            fileUrl: uploadResult.url,
            public_id: uploadResult.public_id,
            resource_type: uploadResult.resource_type || (file.type.startsWith('image/') ? 'image' : 'raw')
          };
          setFormData(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), newAttachment]
          }));
          successCount++;
        } else {
          toast.error(`Failed to upload "${file.name}".`);
          failCount++;
        }
      } catch (err) {
        toast.error(`Error uploading "${file.name}".`);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file(s).`);
    }
    setIsUploading(false);
    e.target.value = '';
  };

  const handleDownloadAttachment = async (e, att) => {
    e.preventDefault();
    e.stopPropagation();
    let loader;
    try {
      const publicId = att.public_id || att.publicId;
      if (!publicId) {
        toast.error("File details not available for download");
        return;
      }

      loader = toast.info("Preparing download...", 30000);
      const fileName = att.fileName || att.filename || 'file';
      const token = localStorage.getItem('auth_token');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const downloadUrl = `${apiBaseUrl}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fileName)}`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      if (loader && typeof loader.remove === 'function') {
        loader.remove();
      }
      toast.success("Download started!");
    } catch (error) {
      if (loader && typeof loader.remove === 'function') {
        loader.remove();
      }
      console.error('Error downloading file:', error);
      toast.error('Failed to download file.');
    }
  };

  const handleRemoveAttachment = async (attachmentToRemove, index) => {
    setDeletingIndex(index);
    try {
      if (attachmentToRemove.public_id) {
        const result = await fileUploadService.deleteFromCloudinary(
          attachmentToRemove.public_id,
          attachmentToRemove.resource_type || 'raw'
        );
        if (!result.success) {
          toast.error(result.error || 'Failed to delete file from storage. Removing from list.');
        }
      }

      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, idx) => idx !== index)
      }));
      toast.success('Attachment removed.');
    } catch (err) {
      toast.error('An error occurred while removing attachment.');
    } finally {
      setDeletingIndex(null);
    }
  };

  // Activity types
  const activityTypes = [
    { value: 'call', label: 'Phone Call', icon: Phone },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'meeting', label: 'Meeting', icon: Calendar },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { value: 'linkedin', label: 'LinkedIn', icon: Linkedin }
  ];

  // Outcomes
  const outcomes = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'rescheduled', label: 'Rescheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];


  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else {
      return 'Unknown Lead';
    }
  };

  const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
    let processedValue = value;
    let error = '';

    if (fieldType === 'text') {
      processedValue = sanitizeTextInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'number') {
      const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
      const minValue = NUMBER_LIMITS[field]?.min || 0;
      const numValidation = validateNumberInput(value, maxValue, minValue);
      processedValue = numValidation.value;
      error = numValidation.error || '';
    } else if (fieldType === 'textarea') {
      processedValue = sanitizeDescriptionInput(String(value || ''));
      processedValue = processedValue.slice(0, maxLength);
    } else if (fieldType === 'datetime-local') {
      // For datetime-local inputs, use the value as-is without sanitization
      processedValue = value || '';
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Set or clear error
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBlur = (field) => {
    let error = '';
    const value = formData[field];

    if (field === 'description') {
      if (!value || value.trim() === '') {
        error = 'Activity description is required';
      }
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Activity description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const activityPayload = {
        type: formData.type,
        description: formData.description.trim(),
        attachments: formData.attachments || []
      };

      // Debug log to trace the payload


      let result;
      if (isEditMode && activityId) {
        // Update existing activity
        result = await leadActivityService.updateActivity(activityId, activityPayload);
        if (result.success) {
          toast.success('Activity updated successfully!');
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(location.state?.from || -1);
          }
        } else {
          toast.error(result.error || 'Failed to update activity. Please try again.');
        }
      } else {
        // Create new activity
        activityPayload.performedAt = new Date().toISOString();

        result = await leadActivityService.createActivity(id, activityPayload);
        if (result.success) {
          toast.success('Activity logged successfully!');
          if (onSuccess) {
            onSuccess();
          } else {
            navigate(location.state?.from || -1);
          }
        } else {
          toast.error(result.error || 'Failed to log activity. Please try again.');
        }
      }

    } catch (error) {

      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Always go back to lead activities page OR call onBack if in drawer
    if (onBack) {
      onBack();
    } else {
      navigate(location.state?.from || -1);
    }
  };

  // Show loading state while fetching lead or activity data
  if (isLoadingLead || isLoadingActivity) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lead information...</p>
        </div>
      </div>
    );
  }

  // Show error state if lead not found
  if (!leadData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Lead not found</p>
          <button
            className="btn-cancel flex items-center justify-center space-x-2"
            onClick={() => navigate(location.state?.from || '/lead_list_filters')}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Leads</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          className="btn-cancel flex items-center justify-center space-x-2"
          onClick={handleCancel}
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Activity' : 'Log Activity'}
          </h1>
          <p className="text-gray-600 break-words">
            {isEditMode ? 'Edit activity details' : `Log an activity for ${getLeadName(leadData)}`}
          </p>
        </div>
      </div>

      {/* Lead Information Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3 min-w-0">
            <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{getLeadName(leadData)}</p>
              <p className="text-xs text-gray-500">Contact Person</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{leadData.company || 'No company'}</p>
              <p className="text-xs text-gray-500">Company</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-all break-words">{leadData.email || 'No email'}</p>
              <p className="text-xs text-gray-500">Email</p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Phone className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 break-words">{leadData.phone || 'No phone'}</p>
              <p className="text-xs text-gray-500">Phone</p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Details</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Activity Type <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {activityTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('type', type.value)}
                    className={`p-3 border rounded-lg text-center transition-colors ${formData.type === type.value
                      ? 'border-brand bg-brand/10 text-brand'
                      : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <IconComponent className="h-5 w-5 mx-auto mb-2" />
                    <span className="text-xs font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>


          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value, 'textarea', FIELD_LIMITS.notes)}
              onBlur={() => handleBlur('description')}
              rows={10}
              className={`input-field ${errors.description ? 'border-red-500 ring-red-200' : ''}`}
              placeholder="Describe what happened during this activity"
              maxLength={FIELD_LIMITS.notes}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
              <span className="text-xs text-gray-500 ml-auto">
                {getCharacterCount(formData.description, FIELD_LIMITS.notes)}
              </span>
            </div>
          </div>

          {/* Attachments Section */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700">
              Attachments (Images / Documents)
            </label>

            {/* File Upload Area */}
            <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-brand hover:bg-brand/5 transition-all bg-gray-50 flex flex-col items-center justify-center cursor-pointer group">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={isUploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
              {isUploading ? (
                <div className="flex flex-col items-center space-y-2 py-2">
                  <Clock className="h-8 w-8 text-brand animate-spin" />
                  <p className="text-sm font-medium text-gray-600">Uploading files to secure storage...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-2 py-2 text-center">
                  <Paperclip className="h-8 w-8 text-gray-400 group-hover:text-brand transition-colors" />
                  <p className="text-sm font-medium text-gray-600 group-hover:text-brand transition-colors">
                    Click or drag files to upload
                  </p>
                  <p className="text-xs text-gray-400">
                    Supports images, PDF, DOCX up to 25MB
                  </p>
                </div>
              )}
            </div>

            {/* List of uploaded files */}
            {formData.attachments && formData.attachments.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {formData.attachments.map((att, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl hover:shadow-sm transition-all">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-brand/10 text-brand rounded-lg flex-shrink-0">
                        {att.resource_type === 'image' || att.fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <Image className="h-5 w-5" />
                        ) : (
                          <FileText className="h-5 w-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate" title={att.fileName}>
                          {att.fileName}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => handleDownloadAttachment(e, att)}
                          className="text-xs text-brand hover:underline font-semibold text-left focus:outline-none"
                        >
                          View File
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(att, idx)}
                      disabled={deletingIndex === idx}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors flex-shrink-0 disabled:opacity-50"
                      title="Remove Attachment"
                    >
                      {deletingIndex === idx ? (
                        <Clock className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <PermissionGate permission="lead_management" action={isEditMode ? "update" : "create"}>
              <button
                type="submit"
                className="btn-create flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Clock className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span>
                  {isSubmitting
                    ? (isEditMode ? 'Updating Activity...' : 'Logging Activity...')
                    : (isEditMode ? 'Update Activity' : 'Log Activity')}
                </span>
              </button>
            </PermissionGate>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogActivity;
